import { useCallback, useEffect, useRef, useState } from "react";
import {
  PaperClipIcon,
  PhotoIcon,
  ArrowTopRightOnSquareIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { Spinner, Typography } from "@material-tailwind/react";
import { useTranslation } from "react-i18next";
import { api } from "../../../services/api/api";

interface PartAttachmentsProps {
  workorderId: string;
  partLineId: string;
  /** Renseigné pour une ligne posée sur une machine : la route d'upload en dépend. */
  machineId?: string;
  canEdit?: boolean;
}

/** Ce que le back accepte (WorkOrderPartAttachmentService) — refusé côté client pour le dire tout de suite. */
const ACCEPTED_MIME = ["image/png", "image/jpeg", "application/pdf"];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Justificatifs d'achat d'une ligne de pièce comptoir (ticket photographié, PDF du fournisseur).
 *
 * La liste est chargée au rendu de la ligne, pour afficher tout de suite les noms de fichiers
 * et l'ajout d'un justificatif supplémentaire.
 *
 * Les octets passent par l'API, comme pour les photos et les captures diag sécu, et non par une
 * URL vers le bucket : les objets y sont chiffrés en SSE-C, dont la clé voyage dans les en-têtes
 * de la requête — un navigateur qui suit un lien ne peut pas la fournir.
 */
export default function PartAttachments({
  workorderId,
  partLineId,
  machineId,
  canEdit = false,
}: PartAttachmentsProps) {
  const { t } = useTranslation();
  const [attachments, setAttachments] = useState<EasyTech.WoPartAttachment[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [rejection, setRejection] = useState<string | null>(null);
  const objectUrls = useRef<string[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(
    () => () => {
      objectUrls.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrls.current = [];
    },
    []
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setHasFailed(false);
    try {
      const response = await api.get(
        `users/workorders/${workorderId}/part/${partLineId}/attachments`
      );
      setAttachments((response as EasyTech.WoPartAttachment[]) ?? []);
    } catch (error) {
      setHasFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [workorderId, partLineId]);

  useEffect(() => {
    load();
  }, [load]);

  const open = async (attachment: EasyTech.WoPartAttachment) => {
    if (openingId) return;
    setOpeningId(attachment.id);
    try {
      const buffer = await api.getPhotos(
        `users/workorders/${workorderId}/part/attachments/${attachment.id}/download`
      );
      const objectUrl = URL.createObjectURL(
        new Blob([buffer], { type: attachment.mimeType || "application/octet-stream" })
      );
      // Gardée jusqu'au démontage : la révoquer tout de suite fermerait l'onglet qu'on vient
      // d'ouvrir.
      objectUrls.current.push(objectUrl);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      setHasFailed(true);
    } finally {
      setOpeningId(null);
    }
  };

  const upload = async (file: File) => {
    setRejection(null);
    // Vérifié ici pour répondre tout de suite, mais c'est le serveur qui fait autorité.
    if (!ACCEPTED_MIME.includes(file.type)) {
      setRejection(t("workorder:parts:attachments:unsupported"));
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setRejection(t("workorder:parts:attachments:tooLarge"));
      return;
    }

    setIsUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const route = machineId
        ? `users/workorders/${workorderId}/machine/part/${partLineId}/attachments`
        : `users/workorders/${workorderId}/part/${partLineId}/attachments`;
      const created = (await api.post(route, body, {
        isMultipart: true,
      })) as EasyTech.WoPartAttachment;
      setAttachments((current) => [...(current ?? []), created]);
    } catch (error) {
      setRejection(t("workorder:parts:attachments:uploadFailed"));
    } finally {
      setIsUploading(false);
    }
  };

  const addButton = canEdit ? (
    <>
      <input
        ref={fileInput}
        type="file"
        accept={ACCEPTED_MIME.join(",")}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Remis à zéro pour que re-choisir le même fichier déclenche encore un change.
          e.target.value = "";
          if (file) upload(file);
        }}
      />
      <button
        type="button"
        onClick={() => fileInput.current?.click()}
        disabled={isUploading}
        className="flex items-center gap-1 text-xs text-blue-600 hover:underline disabled:opacity-60"
      >
        {isUploading ? <Spinner className="h-3 w-3" /> : <PlusIcon className="h-3 w-3" />}
        {t("workorder:parts:attachments:add")}
      </button>
    </>
  ) : null;

  const rejectionNotice = rejection ? (
    <Typography variant="small" className="text-xs text-red-500">
      {rejection}
    </Typography>
  ) : null;

  if (attachments === null) {
    return (
      <div className="flex flex-col gap-1">
        {isLoading ? (
          <Spinner className="h-3 w-3" />
        ) : hasFailed ? (
          <button
            type="button"
            onClick={load}
            className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
          >
            <PaperClipIcon className="h-3 w-3" />
            {t("workorder:parts:attachments:retry")}
          </button>
        ) : null}
        {addButton}
        {rejectionNotice}
      </div>
    );
  }

  if (attachments.length === 0) {
    return (
      <div className="flex flex-col gap-1">
        <Typography variant="small" className="text-xs text-blue-gray-400">
          {t("workorder:parts:attachments:none")}
        </Typography>
        {addButton}
        {rejectionNotice}
      </div>
    );
  }

  return (
    <div className="mt-1 flex flex-col gap-1">
      <ul className="flex flex-col gap-1">
      {attachments.map((attachment) => (
        <li key={attachment.id}>
          <button
            type="button"
            onClick={() => open(attachment)}
            disabled={openingId === attachment.id}
            className="flex w-full min-w-0 items-center gap-1 text-xs text-blue-600 hover:underline disabled:opacity-60"
          >
            {openingId === attachment.id ? (
              <Spinner className="h-3 w-3 shrink-0" />
            ) : attachment.kind === "IMAGE" ? (
              <PhotoIcon className="h-3 w-3 shrink-0" />
            ) : (
              <PaperClipIcon className="h-3 w-3 shrink-0" />
            )}
            <span className="min-w-0 truncate">
              {attachment.displayName || t("workorder:parts:attachments:untitled")}
            </span>
            <ArrowTopRightOnSquareIcon className="h-3 w-3 shrink-0" />
          </button>
        </li>
        ))}
      </ul>
      {addButton}
      {rejectionNotice}
    </div>
  );
}
